import { useState } from "react";
import { Pencil, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ContactSearchCombobox, {
  type ContactSearchInherit,
} from "@/components/Crm/ContactSearchCombobox";
import type { ContactLite } from "@/components/Crm/contact-display";

/**
 * Troca do contato principal da oportunidade.
 *
 * O display continua sendo o link para a ficha do contato — quem clica no nome
 * quer ir para lá. A edição fica num botão-lápis vizinho, que abre um diálogo
 * com o `ContactSearchCombobox` já existente (busca com debounce + criação
 * rápida). Transformar o nome inteiro em gatilho de edição tiraria o único
 * caminho para a ficha.
 *
 * Diálogo, e não popover, porque o combobox já é um popover: aninhar os dois
 * quebra o gerenciamento de foco.
 */
export default function InlineEditContact({
  onCommit,
  canEdit,
  children,
  inherit,
  disabledReason,
}: {
  onCommit: (contactId: number) => Promise<void>;
  canEdit: boolean;
  /** O display atual (normalmente um link com avatar e nome). */
  children: React.ReactNode;
  /** Pré-popula a criação rápida com órgão/cidade da oportunidade. */
  inherit?: ContactSearchInherit;
  disabledReason?: string;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  async function pick(c: ContactLite) {
    setSaving(true);
    try {
      await onCommit(c.id);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="group/contact flex min-w-0 items-center gap-1">
      <div className="min-w-0">{children}</div>
      {canEdit && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          aria-label="Trocar contato principal"
          title={disabledReason ?? "Trocar contato principal"}
          className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/contact:opacity-100"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Pencil className="h-3.5 w-3.5" />
          )}
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Trocar contato principal</DialogTitle>
            <DialogDescription>
              A oportunidade passa a ser vinculada ao contato escolhido. O
              histórico e os envolvidos não mudam.
            </DialogDescription>
          </DialogHeader>
          <ContactSearchCombobox
            value={null}
            onChange={(c) => void pick(c)}
            inherit={inherit}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
